import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useMenuPosition } from '../StatusDropdown/hooks/useMenuPosition';
import { chooseOnPress, commitAfterPaint, hideMenuNow } from '../../../shared/StatusDropdown/hooks/paintChoice';

import './styles/difficulty.css';

/**
 * Difficulty options with their colors
 * Scale: Very Easy → Easy → Medium → Hard → Extreme
 */
export const DIFFICULTY_OPTIONS = [
  { id: 'very_easy', name: 'Very Easy', color: '#22c55e' },
  { id: 'easy', name: 'Easy', color: '#84cc16' },
  { id: 'medium', name: 'Medium', color: '#f59e0b' },
  { id: 'hard', name: 'Hard', color: '#f97316' },
  { id: 'extreme', name: 'Extreme', color: '#ef4444' },
  { id: null, name: 'None', color: '#6b7280' },
];

/**
 * Get difficulty config by ID
 */
export const getDifficultyConfig = (difficultyId) => {
  return DIFFICULTY_OPTIONS.find(d => d.id === difficultyId) || DIFFICULTY_OPTIONS[5]; // Default to 'None'
};

/**
 * DifficultyDropdown Component
 * 
 * Dropdown for selecting task difficulty.
 * Uses portal to render menu outside the transformed canvas.
 */
const DifficultyDropdown = ({
  taskId,
  currentDifficulty,
  onDifficultyChange,
  showLabel = true,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  // Menu positioning hook (reuse from StatusDropdown)
  const { menuPosition, openMenu } = useMenuPosition(btnRef, menuRef, showMenu);

  // Get current difficulty config
  const difficultyConfig = getDifficultyConfig(currentDifficulty);

  // Close menu when clicking outside
  useEffect(() => {
    if (!showMenu) return;

    const handleClickOutside = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setShowMenu(false);
    };

    document.addEventListener('mousedown', handleClickOutside, true);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [showMenu]);

  // Close menu when canvas starts panning (trackpad or mouse drag)
  useEffect(() => {
    if (!showMenu) return;

    const handlePanStart = () => setShowMenu(false);
    window.addEventListener('flowchartpanstart', handlePanStart);
    return () => window.removeEventListener('flowchartpanstart', handlePanStart);
  }, [showMenu]);

  const handleClick = (e) => {
    e.stopPropagation();
    
    if (!showMenu) {
      openMenu(e);
    }
    
    setShowMenu(!showMenu);
  };

  const handleSelect = useCallback((difficultyId) => {
    const option = getDifficultyConfig(difficultyId);
    hideMenuNow(menuRef.current);
    const button = btnRef.current;
    if (button && option) {
      button.style.setProperty('--difficulty-color', option.color);
      button.style.setProperty('--difficulty-bg', `${option.color}15`);
      const label = button.querySelector('.difficulty-dropdown__label');
      const dot = button.querySelector('.difficulty-dropdown__dot');
      if (label) label.textContent = option.name;
      if (dot) dot.style.background = option.color;
    }
    commitAfterPaint(() => {
      onDifficultyChange?.(taskId, difficultyId);
      setShowMenu(false);
    });
  }, [onDifficultyChange, taskId]);

  // Menu content with portal
  const menuContent = showMenu && (
    <div 
      ref={menuRef}
      className="difficulty-dropdown__menu"
      style={{ 
        position: 'fixed',
        top: menuPosition.top, 
        left: menuPosition.left,
        zIndex: 10000,
      }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onWheel={(e) => e.stopPropagation()}
    >
      {DIFFICULTY_OPTIONS.map(option => (
        <button
          key={option.id ?? 'none'}
          className={`difficulty-dropdown__option ${option.id === currentDifficulty ? 'difficulty-dropdown__option--active' : ''}`}
          onMouseDown={(event) => chooseOnPress(event, () => handleSelect(option.id))}
        >
          <span 
            className="difficulty-dropdown__option-dot" 
            style={{ background: option.color }}
          />
          <span className="difficulty-dropdown__option-label">{option.name}</span>
        </button>
      ))}
    </div>
  );

  return (
    <>
      <div className="difficulty-dropdown__row">
        {showLabel && <span className="difficulty-dropdown__field-label">Difficulty</span>}
        <button 
          ref={btnRef}
          className="difficulty-dropdown__btn"
          onClick={handleClick}
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            '--difficulty-color': difficultyConfig.color,
            '--difficulty-bg': `${difficultyConfig.color}15`,
          }}
        >
          <span 
            className="difficulty-dropdown__dot" 
            style={{ background: difficultyConfig.color }}
          />
          <span className="difficulty-dropdown__label">
            {difficultyConfig.name}
          </span>
          <span className="difficulty-dropdown__arrow">▾</span>
        </button>
      </div>

      {showMenu && createPortal(menuContent, document.body)}
    </>
  );
};

export default DifficultyDropdown;
